import React, {useEffect, useMemo, useState} from "react";
import {Button, Dialog, DialogContent, DialogTitle, Grid, List, ListItem, Stack, styled, Typography} from "@mui/material";
import {THEME_CONFIG} from "../../../config";
import {getTranslatedAnswers} from "../../../functions/getTranslatedAnswers";
import useLanguageStore from "../../../store/languageStore";
import CustomConfirmDialog from "../../shared/CustomConfirmDialog";

const {poll, pollBorder, pollTextColor, borderRadius, shadows} = THEME_CONFIG

export default function PhotoMultiPollQuestion({
                                                 risposte,
                                                 risposteTranslations,
                                                 hideResult = false,
                                                 answer,
                                                 answers,
                                                 sendAnswer,
                                                 isPoll,
                                                 votesNumber = 2
                                               }) {
  const {currentLanguage, defaultLanguage} = useLanguageStore();

  const [answered, setAnswered] = useState(answers);
  const [showThankYouDialog, setShowThankYouDialog] = useState(false);

  useEffect(() => {
    setAnswered(answers);
  }, [answers]);

  // Mostra il popup quando i voti residui arrivano a 0
  useEffect(() => {
    if (answered && Array.isArray(answered) && answered.length === parseInt(votesNumber)) {
      setShowThankYouDialog(true);
    }
  }, [answered, votesNumber]);

  // Ottieni le risposte tradotte
  const translatedRisposte = useMemo(() => {
    if (!risposte || !Array.isArray(risposte) || risposte.length === 0) {
      return risposte || [];
    }

    const translated = getTranslatedAnswers(risposte, risposteTranslations, currentLanguage, defaultLanguage);

    if (!Array.isArray(translated)) {
      return risposte;
    }

    // Per PhotoPollQuestion, le risposte possono essere oggetti o stringhe
    // Se abbiamo oggetti, mantieni la struttura originale ma aggiorna solo il label
    // Se risposte è array di oggetti
    if (typeof risposte[0] === 'object' && risposte[0] !== null) {
      // Gestisci ogni elemento individualmente per supportare array misti
      return risposte.map((original, index) => {
        // Assicurati che original sia un oggetto valido
        if (!original || typeof original !== 'object') {
          return original;
        }

        const translatedItem = translated[index];

        // Se la traduzione è una stringa, usa solo per aggiornare il label
        if (typeof translatedItem === 'string') {
          return {
            ...original,
            label: translatedItem || original.label
          };
        }

        // Se la traduzione è un oggetto, mergia mantenendo tutte le proprietà dell'originale
        if (translatedItem && typeof translatedItem === 'object' && translatedItem !== null) {
          return {
            ...original,
            label: translatedItem.label !== undefined && translatedItem.label !== null
              ? String(translatedItem.label)
              : original.label
          };
        }

        // Se non c'è traduzione o è null/undefined, usa l'originale
        return original;
      });
    }

    // Se risposte è array di stringhe, restituisci translated direttamente
    // Assicurati che tutti gli elementi siano stringhe o valori validi
    return translated.map((item, index) => {
      return item !== undefined && item !== null ? item : risposte[index];
    });
  }, [risposte, risposteTranslations, currentLanguage, defaultLanguage]);

  // Trova gli indici delle risposte date (answers) nelle risposte originali
  const answerIndexes = useMemo(() => {
    if (!answered || !Array.isArray(answered) || answered.length === 0) return [];

    return risposte
      .map((r, index) => {
        const originalLabel = typeof r === 'object' ? r.label : r;
        // Verifica se originalLabel è presente nell'array answers
        if (answered.includes(originalLabel)) {
          return index;
        }
        return null;
      })
      .filter(index => index !== null);
  }, [answers, answered, risposte]);
  const RispostaStyled = styled(Typography)({
    border: `1px solid ${pollBorder.default}`,
    borderRadius: borderRadius.default,
    background: poll.default,
    cursor: 'pointer',
    color: pollTextColor.default,
    padding: 8,
    boxShadow: shadows.default,
    textAlign: 'center',
  })

  const computeBkg = (index, isCorrect) => {
    const isSelected = answerIndexes.includes(index);
    if (!isPoll && hideResult === false) {
      if (answerIndexes.length > 0) {
        if (isCorrect) {
          return poll.correct
        } else if (isSelected && !isCorrect) {
          return poll.wrong
        }
      }
    } else {
      if (isSelected) {
        return poll.answered
      }
    }

  }

  const computeBorderColor = (index, isCorrect) => {
    const isSelected = answerIndexes.includes(index);
    if (!isPoll && hideResult === false) {
      if (answerIndexes.length > 0) {
        if (isCorrect) {
          return pollBorder.correct
        } else if (isSelected && !isCorrect) {
          return pollBorder.wrong
        }
      }
    } else {
      if (isSelected) {
        return pollBorder.answered
      }
    }
  }

  const computeTextColor = (index, isCorrect) => {
    const isSelected = answerIndexes.includes(index);
    if (!isPoll && hideResult === false) {
      if (answerIndexes.length > 0) {
        if (isCorrect) {
          return pollTextColor.correct
        } else if (isSelected && !isCorrect) {
          return pollTextColor.wrong
        }
      }
    } else {
      if (isSelected) {
        return pollTextColor.answered
      }
    }
  }

  const Risposte = () => (
    translatedRisposte.map((r, i) => {
      // Assicurati che r sia sempre un oggetto o una stringa valida
      if (!r || (typeof r !== 'object' && typeof r !== 'string') || (typeof r === 'object' && !r)) {
        return null;
      }

      const isPicturesPoll = typeof r === 'object' && r !== null ? !!r?.image?.url : false;
      const isCorrect = typeof r === 'object' && r !== null ? r?.isCorrect : false;
      // Estrai il testo in modo sicuro: sempre una stringa
      let rispText = "";
      if (typeof r === 'object' && r !== null) {
        // Se è un oggetto, estrai il label
        const label = r?.label;
        rispText = (typeof label === 'string' ? label : String(label || "")) || "";
      } else if (typeof r === 'string') {
        rispText = r;
      } else {
        // Fallback: converti tutto in stringa
        rispText = String(r || "");
      }

      // Assicurati che rispText sia sempre una stringa prima del rendering
      if (typeof rispText !== 'string') {
        rispText = String(rispText || "");
      }
      const isSelected = answerIndexes.includes(i);
      return <Grid key={i} item xs={isPicturesPoll ? 6 : 12}
                   sx={{
                     scale: isPicturesPoll ? answerIndexes.length > 0 ? isSelected ? '1.05' : '0.9' : '1' : 'inherit',
                     opacity: (isCorrect && !hideResult) ? '1' : isPicturesPoll ? answerIndexes.length > 0 ? isSelected ? '1' : answered.length === parseInt(votesNumber) ? '0.5' : '1' : '1' : 'inherit',
                   }}>
        <CustomConfirmDialog
          confirmAction={() => rispondi(i)}
          disabled={answered.length === parseInt(votesNumber)}
          confirmText={<>Assegna un voto a <strong>{rispText}</strong></>}
          label={
          <RispostaStyled key={i} style={{
            width: '100%',
            background: computeBkg(i, isCorrect),
            border: computeBorderColor(i, isCorrect) ? '1px solid ' + computeBorderColor(i, isCorrect) : `1px solid ${pollBorder.default}`,
            color: computeTextColor(i, isCorrect) ? computeTextColor(i, isCorrect) : pollTextColor.default,
            margin: isPicturesPoll ? 'inherit' : '4px 0',
          }}
          >
            <Stack>
              {
                isPicturesPoll &&
                <img
                  src={r?.image?.url}
                  alt={rispText}
                  style={{
                    aspectRatio: '1/1',
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: borderRadius.default,
                  }}/>
              }
              {rispText && <Typography sx={{mt: isPicturesPoll ? 1 : 'inherit'}} textAlign={"center"} fontWeight={'bolder'}
                                       variant={isPicturesPoll ? 'body1' : 'h6'}
                                       whiteSpace='pre-line'>
                {rispText}
              </Typography>}
            </Stack>
          </RispostaStyled>
        }/>
      </Grid>
    })
  )

  const rispondi = (index) => {
    // Usa l'indice per ottenere la risposta originale nella lingua di default
    const originalRisposta = risposte[index];
    const originalAnswer = typeof originalRisposta === 'object' ? originalRisposta.label : originalRisposta;

    if (answered.length < parseInt(votesNumber) && !answers.includes(originalAnswer)) {
      if (isPoll) {
        const updatedAnswers = [...answered, originalAnswer];
        setAnswered(updatedAnswers);
        sendAnswer(originalAnswer)
      } else {
        if (answer === '') {
          sendAnswer(originalAnswer)
        }
      }
    }
  }

  const isPicturesPoll = translatedRisposte && translatedRisposte[0] && !!translatedRisposte[0]?.image?.url

  return (
    <>
      <List>
          <Grid pb={3} container justifyContent={'center'} width={'100%'}>
            <Typography component={Grid} item xs={12} sx={{my: 1}} variant={'body2'}>Hai ancora {parseInt(votesNumber) - answered.length} vot{parseInt(votesNumber) - answered.length !== 1 ? "i" : "o"}</Typography>
            <Risposte/>
          </Grid>
      </List>
      <Dialog
        open={showThankYouDialog}
        onClose={() => setShowThankYouDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography textAlign="center" variant="h5">
            Grazie per aver votato!
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} alignItems="center" sx={{py: 2}}>
            <Button
              variant="contained"
              onClick={() => setShowThankYouDialog(false)}
              fullWidth
            >
              Chiudi
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  )
}
